import { useTranslation } from "react-i18next";

import type { ShowcaseStats } from "@/entities/robot";
import { formatPercent, percentTone } from "@/shared/lib";
import { Icon, Panel } from "@/shared/ui";
import type { IconName } from "@/shared/ui";

/**
 * Плитки статистики (ТЗ §8).
 *
 * Все шесть цифр считаются из ОДНОГО витринного ряда — того же, что рисует
 * график выше. Поэтому «Historical Return» здесь равен крупной цифре в шапке
 * не по договорённости, а по построению.
 *
 * «Прибыльных дней» вместо «Win Rate» — сознательная правка формулировки ТЗ.
 * Бэкенд считает долю плюсовых ДНЕЙ, а не сделок; подпись «Win Rate» рядом с
 * плиткой «Сделок за год» читалась бы как доля прибыльных сделок, то есть
 * обещала бы не то, что означает.
 */
const TILES: { key: string; icon: IconName }[] = [
  { key: "historicalReturn", icon: "trendUp" },
  { key: "maxDrawdown", icon: "arrowCircleDown" },
  { key: "profitableMonths", icon: "checkFat" },
  { key: "winRate", icon: "chartLineUp" },
  { key: "trades", icon: "pulse" },
];

/**
 * Плиток «Пользователей» и «Под управлением» здесь больше нет.
 *
 * Первая показывала витринную цифру оператора (`stats.users_count`), а блок
 * ключевых метрик под шапкой — настоящих активных пользователей: на одном
 * экране это два разных числа под одним словом.
 *
 * Вторая переехала в тот же блок ключевых метрик — туда её ставит §4.1
 * доработок. Оставить её и здесь значило бы показать одно число дважды и
 * заставить сверять, не разошлись ли копии; к тому же капитал приходит из
 * карточки, а не из аналитики, и здесь он пропадал бы вместе с ней.
 */
export function StatCards({
  stats,
  showWinRate = true,
}: {
  stats: ShowcaseStats;
  showWinRate?: boolean;
}) {
  const { t } = useTranslation();

  const values: Record<string, { text: string; tone?: string }> = {
    historicalReturn: {
      text: formatPercent(stats.historical_yield_12m, { sign: "always" }),
      tone: percentTone(stats.historical_yield_12m),
    },
    // Просадка приходит положительным числом — знак ставится здесь, иначе
    // «11.40%» рядом со словом «просадка» читается как рост.
    maxDrawdown: { text: `−${formatPercent(stats.max_drawdown)}`, tone: "text-status-loss" },
    profitableMonths: {
      text: t("robots.analytics.stats.profitableMonthsValue", {
        count: stats.profitable_months,
        total: stats.months_total,
      }),
    },
    winRate: { text: formatPercent(stats.win_rate) },
    trades: { text: String(stats.trades_count) },
  };

  // Плитка исчезает, когда её выключил оператор (ТЗ §26).
  const tiles = TILES.filter((tile) => (tile.key === "winRate" ? showWinRate : true));

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-body font-semibold text-text-primary">
        {t("robots.analytics.stats.title")}
      </h2>
      <ul className="grid grid-cols-2 gap-2">
        {tiles.map(({ key, icon }) => (
          <Panel as="li" key={key} radius="lg" className="flex min-w-0 flex-col gap-1.5 p-3">
            <span className="flex items-center gap-1.5 text-micro font-medium text-text-tertiary">
              <Icon name={icon} size={12} />
              <span className="truncate">{t(`robots.analytics.stats.${key}`)}</span>
            </span>
            <span
              className={`text-body font-semibold ${values[key]?.tone ?? "text-text-primary"}`}
            >
              {values[key]?.text}
            </span>
          </Panel>
        ))}
      </ul>
      <p className="text-micro font-normal text-text-tertiary">
        {t("robots.analytics.stats.winRateHint")}
      </p>
    </section>
  );
}
